import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getProjects, createProject, updateProject, deleteProject } from '../services/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Image } from 'lucide-react';

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title_en: '', title_zh: '',
    location_en: '', location_zh: '',
    date: '', before_image: '', after_image: '',
    category: 'residential', active: true
  });

  const fetchProjects = async () => {
    try {
      const res = await getProjects();
      setProjects(res.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData(project);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingProject(null);
    setFormData({
      title_en: '', title_zh: '',
      location_en: '', location_zh: '',
      date: new Date().getFullYear().toString(),
      before_image: '', after_image: '',
      category: 'residential', active: true
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editingProject) {
        await updateProject(editingProject.id, formData);
        toast.success('Project updated');
      } else {
        await createProject(formData);
        toast.success('Project created');
      }
      fetchProjects();
      setShowModal(false);
    } catch (error) {
      toast.error('Failed to save project');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await deleteProject(id);
      setProjects(projects.filter(p => p.id !== id));
      toast.success('Project deleted');
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleToggleActive = async (project) => {
    try {
      await updateProject(project.id, { active: !project.active });
      setProjects(projects.map(p => p.id === project.id ? { ...p, active: !p.active } : p));
    } catch (error) {
      toast.error('Failed to update project');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Projects</h1>
          <p className="text-slate-600">Manage your portfolio with before/after images</p>
        </div>
        <Button onClick={handleCreate} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
          <Plus className="w-4 h-4 mr-2" /> Add Project
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {projects.map((project) => (
          <Card key={project.id} className={`overflow-hidden bg-white border-2 border-slate-300 ${!project.active ? 'opacity-50' : ''}`}>
            <div className="grid grid-cols-2 h-32">
              {project.before_image ? (
                <img src={project.before_image} alt="Before" className="w-full h-full object-cover" />
              ) : (
                <div className="bg-slate-200 flex items-center justify-center"><Image className="w-8 h-8 text-slate-400" /></div>
              )}
              {project.after_image ? (
                <img src={project.after_image} alt="After" className="w-full h-full object-cover" />
              ) : (
                <div className="bg-slate-200 flex items-center justify-center"><Image className="w-8 h-8 text-slate-400" /></div>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-semibold text-slate-900">{project.title_en}</h3>
                <span className="text-slate-400">|</span>
                <span className="text-slate-600">{project.title_zh}</span>
              </div>
              <p className="text-sm text-slate-500">{project.location_en} • {project.date}</p>
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <Switch checked={project.active} onCheckedChange={() => handleToggleActive(project)} />
                  <span className="text-xs text-slate-500">{project.active ? 'Visible' : 'Hidden'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(project)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleDelete(project.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Project Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProject ? 'Edit Project' : 'Add New Project'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title (English)</label>
                <Input value={formData.title_en} onChange={(e) => setFormData({ ...formData, title_en: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Title (Chinese)</label>
                <Input value={formData.title_zh} onChange={(e) => setFormData({ ...formData, title_zh: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Location (English)</label>
                <Input value={formData.location_en} onChange={(e) => setFormData({ ...formData, location_en: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Location (Chinese)</label>
                <Input value={formData.location_zh} onChange={(e) => setFormData({ ...formData, location_zh: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Year</label>
                <Input value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <Input value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} placeholder="residential, commercial, repair" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Before Image URL</label>
              <Input value={formData.before_image} onChange={(e) => setFormData({ ...formData, before_image: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">After Image URL</label>
              <Input value={formData.after_image} onChange={(e) => setFormData({ ...formData, after_image: e.target.value })} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={formData.active} onCheckedChange={(checked) => setFormData({ ...formData, active: checked })} />
              <span className="text-sm">Active (visible on website)</span>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600 text-slate-900">Save Project</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProjects;
